import React, { useEffect, useState } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import { Button, DatePicker, Input, Switch, Typography } from 'antd';
// Hooks
import { useAppContext, useGetMyEntitlements } from 'hooks';
// Helpers
import {
  formatEntitlementType,
  mapEntitlementTypenameToType,
} from 'helpers/brands';
import {
  NUMERIC_ENTITLEMENTS,
  TOGGLE_ENTITLEMENTS,
  TOGGLE_ENTITLEMENT_TYPES,
  collectEntitlementUpdates,
  findEntitlement,
  getEntitlementInputError,
  getNumericEntitlementValue,
  isEntitlementToggleEnabled,
  isNumericEntitlementVisible,
} from 'helpers/brandEntitlements';
import { getProperErrorMessage } from 'helpers/errors';
// Types
import { SetCbEntitlementInput } from 'api/graphql-global-types';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './EntitlementsSection.module.scss';

const { Title, Text } = Typography;

const TOGGLE_ENABLED_YEARS = 100;

type EntitlementsSectionProps = {
  ownerId: string;
  setEntitlement: (variables: {
    input: SetCbEntitlementInput;
  }) => Promise<unknown>;
};

const EntitlementsSection: React.FC<EntitlementsSectionProps> = ({
  ownerId,
  setEntitlement,
}) => {
  const { authUser } = useAppContext();
  const [entitlementDates, setEntitlementDates] = useState<
    Record<string, string | null>
  >({});
  const [numericInputs, setNumericInputs] = useState<Record<string, string>>(
    {}
  );
  const [numericDisplays, setNumericDisplays] = useState<
    Record<string, number>
  >({});
  const [toggleValues, setToggleValues] = useState<Record<string, boolean>>({});
  const [submitLoading, setSubmitLoading] = useState(false);

  const { data: entitlementsData, refetch: refetchEntitlements } =
    useGetMyEntitlements({
      ownerId,
      skip: !ownerId,
    });

  const entitlements = entitlementsData?.getMyEntitlements;

  useEffect(() => {
    if (!entitlements) {
      return;
    }

    const nextDates: Record<string, string | null> = {};
    entitlements.forEach((entitlement) => {
      nextDates[entitlement.id] = entitlement.validUntil || null;
    });
    setEntitlementDates(nextDates);

    const nextInputs: Record<string, string> = {};
    const nextDisplays: Record<string, number> = {};
    NUMERIC_ENTITLEMENTS.forEach((config) => {
      const entitlement = findEntitlement(
        entitlements,
        config.type,
        config.typename
      );
      const value = getNumericEntitlementValue(entitlement, config.field);
      nextInputs[config.type] = value.toString();
      nextDisplays[config.type] = value;
    });
    setNumericInputs(nextInputs);
    setNumericDisplays(nextDisplays);

    const nextToggles: Record<string, boolean> = {};
    TOGGLE_ENTITLEMENTS.forEach((config) => {
      const entitlement = findEntitlement(
        entitlements,
        config.type,
        config.typename
      );
      nextToggles[config.type] = isEntitlementToggleEnabled(entitlement);
    });
    setToggleValues(nextToggles);
  }, [entitlements]);

  if (!entitlements) {
    return null;
  }

  const handleEntitlementDateChange = (
    entitlementId: string,
    date: moment.Moment | null
  ) => {
    setEntitlementDates((prev) => ({
      ...prev,
      [entitlementId]: date ? date.toISOString() : null,
    }));
  };

  const handleSubmitEntitlements = async () => {
    if (!ownerId || !authUser?.id) {
      return;
    }

    const updates = collectEntitlementUpdates({
      entitlements,
      entitlementDates,
      numericInputs,
      toggleValues,
      ownerId,
      performedById: authUser.id,
      toggleEnabledUntil: dayjs()
        .add(TOGGLE_ENABLED_YEARS, 'year')
        .toISOString(),
    });

    const inputError = getEntitlementInputError(
      entitlements,
      numericInputs,
      updates
    );
    if (inputError) {
      errorNotification(inputError);
      return;
    }

    if (updates.length === 0) {
      return;
    }

    setSubmitLoading(true);
    try {
      await Promise.all(updates.map((input) => setEntitlement({ input })));
      successNotification('Entitlements updated successfully!');
      await refetchEntitlements();
    } catch (err: any) {
      errorNotification(
        getProperErrorMessage(
          err,
          err?.message || 'Failed to update entitlements'
        )
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  const dateBasedEntitlements = entitlements.filter(
    (entitlement) => !TOGGLE_ENTITLEMENT_TYPES.includes(entitlement.type)
  );

  return (
    <div className={styles.root}>
      <Title level={5}>Entitlements</Title>
      <div className={styles.card}>
        {dateBasedEntitlements.map((entitlement) => (
          <div key={entitlement.id} className={styles.entitlementItem}>
            <Text strong className={styles.entitlementLabel}>
              {formatEntitlementType(
                mapEntitlementTypenameToType(entitlement.__typename)
              )}
            </Text>
            <DatePicker
              className={styles.dateInput}
              value={
                entitlementDates[entitlement.id]
                  ? moment(entitlementDates[entitlement.id] as string)
                  : undefined
              }
              onChange={(date) =>
                handleEntitlementDateChange(entitlement.id, date)
              }
              placeholder="Set end date"
              format="MM/DD/YYYY"
              allowClear
            />
          </div>
        ))}
        {TOGGLE_ENTITLEMENTS.map((config) => (
          <div key={config.type} className={styles.entitlementItem}>
            <Text strong className={styles.entitlementLabel}>
              {formatEntitlementType(config.type)}
            </Text>
            <Switch
              checked={Boolean(toggleValues[config.type])}
              onChange={(checked) =>
                setToggleValues((prev) => ({
                  ...prev,
                  [config.type]: checked,
                }))
              }
            />
          </div>
        ))}
        {NUMERIC_ENTITLEMENTS.filter((config) =>
          isNumericEntitlementVisible(entitlements, config)
        ).map((config) => (
          <div key={config.type} className={styles.numericSection}>
            <div>
              <Text strong>{config.label}: </Text>
              <Text>{numericDisplays[config.type] ?? 0}</Text>
            </div>
            <div className={styles.numericInputWrapper}>
              <Input
                type="number"
                min={config.min}
                value={numericInputs[config.type] ?? '0'}
                onChange={(e) => {
                  // Read before the updater runs: React 16 pools the event
                  const { value } = e.target;
                  setNumericInputs((prev) => ({
                    ...prev,
                    [config.type]: value,
                  }));
                }}
                placeholder={config.placeholder}
                className={styles.numericInput}
              />
            </div>
          </div>
        ))}
      </div>
      <div className={styles.buttonWrapper}>
        <Button
          type="primary"
          onClick={handleSubmitEntitlements}
          loading={submitLoading}
        >
          Update Entitlements
        </Button>
      </div>
    </div>
  );
};

export default EntitlementsSection;
